"use client";

import { useId } from "react";
import { PHONE_FORMAT } from "@shared/formats/phone";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type {
  GuestContactState,
  UpdateQuoteFormState,
} from "./listing-quote-request-state";
import type { QuoteRequirementErrors } from "./listing-quote-requirements";

interface GuestContactFieldsProps {
  contact: GuestContactState;
  updateState: UpdateQuoteFormState;
  onEdit: () => void;
  errors: QuoteRequirementErrors;
  includeEmail: boolean;
}

export function ListingGuestContactFields({
  contact,
  updateState,
  onEdit,
  errors,
  includeEmail,
}: GuestContactFieldsProps) {
  const idPrefix = useId();
  const setContact = (patch: Partial<GuestContactState>) => {
    onEdit();
    updateState({ contact: { ...contact, ...patch }, submitAttempted: false });
  };
  return (
    <fieldset className="space-y-3">
      <ContactFieldsHeading includeEmail={includeEmail} />
      <ContactNameRow
        idPrefix={idPrefix}
        contact={contact}
        setContact={setContact}
        error={errors.name}
      />
      {includeEmail ? (
        <ContactField
          id={`${idPrefix}-email`}
          label="Email"
          type="email"
          autoComplete="email"
          value={contact.email}
          requirement="email"
          error={errors.email}
          onChange={(email) => setContact({ email })}
        />
      ) : null}
      <ContactField
        id={`${idPrefix}-phone`}
        label="Phone"
        type="tel"
        autoComplete="tel"
        placeholder={PHONE_FORMAT.display}
        value={contact.phone}
        requirement="phone"
        error={errors.phone}
        onChange={(phone) => setContact({ phone: PHONE_FORMAT.format(phone) })}
      />
    </fieldset>
  );
}

function ContactFieldsHeading({ includeEmail }: { includeEmail: boolean }) {
  return (
    <>
      <legend className="text-xs font-medium text-foreground">
        {includeEmail ? "Where should we send the quote?" : "Your contact details"}
      </legend>
    </>
  );
}

function ContactNameRow({
  idPrefix,
  contact,
  setContact,
  error,
}: {
  idPrefix: string;
  contact: GuestContactState;
  setContact: (patch: Partial<GuestContactState>) => void;
  error: string | undefined;
}) {
  const errorId = `${idPrefix}-name-error`;
  const invalid = (value: string) => (error && !value.trim() ? errorId : null);
  return (
    <div className="space-y-1" data-quote-requirement="name">
      <div className="grid grid-cols-2 gap-3">
        <ContactInput
          id={`${idPrefix}-first`}
          label="First name"
          autoComplete="given-name"
          value={contact.firstName}
          errorId={invalid(contact.firstName)}
          onChange={(firstName) => setContact({ firstName })}
        />
        <ContactInput
          id={`${idPrefix}-last`}
          label="Last name"
          autoComplete="family-name"
          value={contact.lastName}
          errorId={invalid(contact.lastName)}
          onChange={(lastName) => setContact({ lastName })}
        />
      </div>
      <FieldError id={errorId} message={error} />
    </div>
  );
}

interface ContactInputProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: "text" | "email" | "tel";
  autoComplete: string;
  placeholder?: string;
}

function ContactField({
  requirement,
  error,
  ...input
}: ContactInputProps & {
  requirement: "email" | "phone";
  error: string | undefined;
}) {
  const errorId = `${input.id}-error`;
  return (
    <div className="space-y-1" data-quote-requirement={requirement}>
      <ContactInput {...input} errorId={error ? errorId : null} />
      <FieldError id={errorId} message={error} />
    </div>
  );
}

function ContactInput({
  id,
  label,
  value,
  onChange,
  errorId,
  type = "text",
  autoComplete,
  placeholder,
}: ContactInputProps & { errorId: string | null }) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id} size="sm">
        {label}
      </Label>
      <Input
        id={id}
        type={type}
        autoComplete={autoComplete}
        placeholder={placeholder}
        value={value}
        aria-invalid={errorId ? true : undefined}
        aria-describedby={errorId ?? undefined}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

function FieldError({ id, message }: { id: string; message: string | undefined }) {
  if (!message) return null;
  return (
    <p id={id} className="text-xs text-destructive-strong">
      {message}
    </p>
  );
}
